import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRememberMe, getRememberedEmail } from '@shared/lib/remember-me';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button, Checkbox, Input, Label, StatusBanner, useToast } from '@shared/ui';
import { validateEmail, validatePassword } from '@entities/auth';
import { useLogin } from '@features/auth/login';
import { FORGOT_PASSWORD_ENABLED } from '@shared/config/feature-flags';
import { AuthScreen } from '@widgets/auth/auth-screen';
import { LoginChallenge, type LoginChallengeState } from '@widgets/auth/login-challenge';
import { consumeSecurityNotice } from '@shared/lib/security-notice';
import { formatDateTime } from '@shared/lib/format-date-time';
import { ForgotPasswordPanel } from './forgot-password-panel';

let passwordResetNoticeConsumed = false;

interface LoginPageProps {
  onNeedEmailVerification?: (email?: string) => void;
  onNeed2FA?: () => void;
  onSwitchToRegister?: () => void;
}

export function LoginPage({
  onNeedEmailVerification,
  onSwitchToRegister,
}: LoginPageProps) {
  const { t, i18n } = useTranslation('auth');
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useLogin();

  const [panel, setPanel] = useState<'login' | 'forgot'>(
    location.pathname.startsWith('/forgot-password') ? 'forgot' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [challenge, setChallenge] = useState<LoginChallengeState | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  useEffect(() => {
    const rememberedEmail = getRememberedEmail();
    const rememberMeEnabled = getRememberMe();

    if (rememberMeEnabled && rememberedEmail) {
      setEmail(rememberedEmail);
    }

    setRememberMe(rememberMeEnabled);
  }, []);

  useEffect(() => {
    const notice = consumeSecurityNotice();
    if (notice === 'SESSION_ENVIRONMENT_CHANGED') {
      setSessionNotice(t('security.sessionEnvironment'));
    } else if (notice === 'SESSION_IP_CHANGED') {
      setSessionNotice(t('security.sessionIp'));
    } else if (notice === 'TWO_FA_SETUP_REQUIRED') {
      setSessionNotice(t('security.sessionSetup'));
    }
  }, [t]);

  useEffect(() => {
    if (location.pathname.startsWith('/forgot-password')) {
      setPanel('forgot');
      return;
    }
    document.title = 'Paygatex Cabinet';
  }, [location.pathname]);

  useEffect(() => {
    const notice = (location.state as { notice?: string } | null)?.notice;
    if (notice !== 'passwordReset') {
      passwordResetNoticeConsumed = false;
      return;
    }
    if (passwordResetNoticeConsumed) {
      return;
    }

    passwordResetNoticeConsumed = true;
    toast(t('recovery.resetSuccess'), 'success');
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, t, toast]);

  const emailValidationResult = validateEmail(email);
  const passwordValidationResult = validatePassword(password);
  const isFormValid = emailValidationResult.isValid && passwordValidationResult.isValid;

  const openForgot = () => {
    setPanel('forgot');
  };

  const backToLogin = () => {
    setPanel('login');
    if (location.pathname.startsWith('/forgot-password')) {
      navigate('/auth', { replace: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    if (!emailValidation.isValid || !passwordValidation.isValid) {
      setErrors({
        email: emailValidation.error ? t(emailValidation.error) : undefined,
        password: passwordValidation.error ? t(passwordValidation.error) : undefined,
      });
      return;
    }

    setErrors({});

    const result = await login(email, password, rememberMe);

    if (result.success) {
      if (result.action === 'confirm_email') {
        onNeedEmailVerification?.(email);
      } else if (result.challenge && (result.action === 'confirm_otp' || result.action === 'register_otp')) {
        setChallenge({
          token: result.challenge.token,
          action: result.action,
          methods: result.challenge.methods,
          sentTo: result.challenge.sentTo,
          resendAfterSec: result.challenge.resendAfterSec,
        });
      }
      return;
    }

    if (result.lockedUntil) {
      setLockedUntil(result.lockedUntil);
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  return (
    <AuthScreen>
      {panel === 'forgot' ? (
        <ForgotPasswordPanel onBack={backToLogin} />
      ) : challenge ? (
        <LoginChallenge
          challenge={challenge}
          onBack={() => setChallenge(null)}
          onLocked={(until) => {
            setChallenge(null);
            setLockedUntil(until ?? null);
          }}
        />
      ) : lockedUntil ? (
        <div className="flex w-full flex-col gap-4">
          <h2 className="auth-step-title">{t('security.loginLocked')}</h2>
          <p className="text-sm text-muted-foreground">{formatDateTime(lockedUntil, i18n.language)}</p>
          <Button type="button" variant="outline" onClick={openForgot}>
            {t('forgotPassword')}
          </Button>
        </div>
      ) : (
        <>
          {sessionNotice ? (
            <StatusBanner variant="destructive" className="w-full">
              {sessionNotice}
            </StatusBanner>
          ) : null}
          <div className="w-full space-y-3 text-center lg:text-left">
            <h2 className="auth-panel-title">{t('welcomeBack')}</h2>
            <p className="auth-panel-description">{t('signInToAccount')}</p>
          </div>

          <form className="flex w-full flex-col items-center gap-6" onSubmit={handleSubmit}>
            <div className="flex w-full flex-col items-center gap-4 self-stretch">
              <div className="w-full">
                <Input
                  type="email"
                  placeholder={t('email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={<Mail className="h-4 w-4" />}
                  error={errors.email}
                  disabled={isLoading}
                />
              </div>

              <div className="relative w-full">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={<Lock className="h-4 w-4" />}
                  error={errors.password}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className={`flex items-center self-stretch ${FORGOT_PASSWORD_ENABLED ? 'justify-between' : ''}`}>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <Label htmlFor="remember" className="cursor-pointer text-xs text-foreground">
                  {t('rememberMe')}
                </Label>
              </div>
              {FORGOT_PASSWORD_ENABLED && (
                <button
                  type="button"
                  className="text-sm text-muted-foreground underline transition-colors hover:text-foreground"
                  onClick={openForgot}
                >
                  {t('forgotPassword')}
                </button>
              )}
            </div>

            <Button
              type="submit"
              variant="default"
              className="w-full"
              loading={isLoading}
              disabled={!isFormValid}
            >
              {t('logIn')}
            </Button>
          </form>

          <div className="text-center">
            <span className="text-sm text-muted-foreground">{t('dontHaveAccount')} </span>
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-sm font-semibold text-primary hover:underline"
            >
              {t('signUp')}
            </button>
          </div>
        </>
      )}
    </AuthScreen>
  );
}
